import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { CheckCircle, Sparkle } from "@phosphor-icons/react";

/* ===========================================================================
   DemoRequestDialog — Path B Phase 2 (2026-02-25)
   ---------------------------------------------------------------------------
   Short B2B demo capture form. Sits in front of the full ApplyWizard as the
   sales-led entry point — Sales/KAM picks up the lead from /coe/sales/b2b-leads,
   qualifies them on a call, then issues a magic-link to the full application.
   Posts to /api/b2b-leads. ~30 second submit.
   =========================================================================== */

const ROLE_OPTIONS = [
    // Order matters here — putting "GP / General dentist" first signals the
    // page welcomes both audiences and isn't an ortho-only funnel (the
    // 2026-02-28 inclusive-language audit reordered this from ortho-first).
    { value: "gp_dentist", label: "GP / General dentist" },
    { value: "orthodontist", label: "Orthodontist" },
    { value: "clinic_admin", label: "Clinic owner / admin" },
    { value: "other", label: "Other" },
];

const CASES_OPTIONS = [
    { value: "0-5", label: "0 – 5" },
    { value: "5-15", label: "5 – 15" },
    { value: "15-30", label: "15 – 30" },
    { value: "30+", label: "30+" },
];

const EMPTY = {
    clinic: "",
    contact_name: "",
    role: "",
    email: "",
    phone: "",
    city: "",
    cases_per_month: "",
    current_aligner_brand: "",
    message: "",
};


export default function DemoRequestDialog({ open, onOpenChange }) {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState(false);
    const [duplicate, setDuplicate] = useState(false);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const reset = () => {
        // Defer the reset so the success view stays visible until the dialog
        // animation completes
        setTimeout(() => {
            setForm(EMPTY);
            setDone(false);
            setDuplicate(false);
            setSending(false);
        }, 200);
    };

    const handleOpenChange = (next) => {
        onOpenChange?.(next);
        if (!next) reset();
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!form.clinic.trim() || !form.contact_name.trim() || !form.role || !form.email.trim() || !form.phone.trim()) {
            toast.error("Please fill the required fields");
            return;
        }
        setSending(true);
        try {
            const r = await api.post("/b2b-leads", form);
            setDone(true);
            setDuplicate(!!r.data.duplicate);
            toast.success(r.data.duplicate ? "We already have your details — your rep will be in touch." : "Demo request received");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to send. Please try again.");
        }
        setSending(false);
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="demo-dialog">
                {done ? (
                    <SuccessView duplicate={duplicate} onClose={() => handleOpenChange(false)} />
                ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="demo-form">
                        <DialogHeader>
                            <div className="overline text-primary mb-1.5 flex items-center gap-1.5">
                                <Sparkle size={11} weight="fill" /> Request a demo
                            </div>
                            <DialogTitle className="font-display text-2xl font-bold tracking-tight">
                                Let's set up a 15-minute call.
                            </DialogTitle>
                            <DialogDescription className="text-sm">
                                Your Invisique™ partnership rep will reach out within 1 business day to talk
                                commercials, case mix, and how Invisique™ fits your practice.
                            </DialogDescription>
                        </DialogHeader>

                        <div className="grid sm:grid-cols-2 gap-4">
                            <DField label="Clinic name *" testid="demo-clinic"
                                value={form.clinic} onChange={(v) => upd("clinic", v)} />
                            <DField label="Your name *" testid="demo-contact-name"
                                value={form.contact_name} onChange={(v) => upd("contact_name", v)} />
                            <DSelect label="Your role *" testid="demo-role"
                                value={form.role} onChange={(v) => upd("role", v)} options={ROLE_OPTIONS} />
                            <DField label="City" testid="demo-city"
                                value={form.city} onChange={(v) => upd("city", v)} />
                            <DField label="Email *" type="email" testid="demo-email"
                                value={form.email} onChange={(v) => upd("email", v)} />
                            <DField label="Phone *" testid="demo-phone"
                                value={form.phone} onChange={(v) => upd("phone", v)} />
                            <DSelect label="Cases per month" testid="demo-cases"
                                value={form.cases_per_month} onChange={(v) => upd("cases_per_month", v)} options={CASES_OPTIONS} />
                            <DField label="Current aligner brand" testid="demo-current-brand"
                                value={form.current_aligner_brand} onChange={(v) => upd("current_aligner_brand", v)}
                                placeholder="None / Invisalign / ClearCorrect / in-house" />
                        </div>

                        <div>
                            <Label>Anything specific you'd like to discuss? (optional)</Label>
                            <Textarea
                                value={form.message}
                                onChange={(e) => upd("message", e.target.value)}
                                rows={3}
                                className="mt-1.5"
                                data-testid="demo-message"
                            />
                        </div>

                        <div className="flex items-center gap-2 pt-2">
                            <Button
                                type="button" variant="ghost"
                                onClick={() => handleOpenChange(false)}
                                className="rounded-full"
                                data-testid="demo-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={sending}
                                className="flex-1 rounded-full h-11"
                                data-testid="demo-submit-btn"
                            >
                                {sending ? "Sending…" : "Request demo"}
                            </Button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
}


function SuccessView({ duplicate, onClose }) {
    return (
        <div className="text-center py-8" data-testid="demo-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 grid place-items-center mb-5">
                <CheckCircle size={32} weight="duotone" className="text-primary" />
            </div>
            <h3 className="font-display text-2xl font-bold mb-2">
                {duplicate ? "We already have your details." : "Thank you — we'll be in touch."}
            </h3>
            <p className="text-sm text-muted-foreground max-w-sm mx-auto mb-6">
                {duplicate
                    ? "Your Invisique™ partnership rep will follow up on your existing request shortly."
                    : "A partnership rep will reach out within 1 business day. In the meantime, feel free to apply directly if you're already decided."}
            </p>
            <Button onClick={onClose} className="rounded-full" data-testid="demo-success-close-btn">Done</Button>
        </div>
    );
}


function DField({ label, value, onChange, type = "text", testid, placeholder }) {
    return (
        <div>
            <Label>{label}</Label>
            <Input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="h-11 mt-1.5"
                data-testid={testid}
            />
        </div>
    );
}

function DSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label>{label}</Label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-11 mt-1.5 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                data-testid={testid}
            >
                <option value="">Select…</option>
                {options.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                ))}
            </select>
        </div>
    );
}
